import type { PropertyTypology, PropertyTypologyInput, TypologyLevel } from '@aimmo/contracts';
import {
  TYPOLOGY_LEVELS,
  TYPOLOGY_LEVEL_LABELS,
  enumOptions,
  propertyTypologyCreateSchema,
} from '@aimmo/contracts';
import { Badge, defineResource, resourceKeys, tsRestResourceClient } from '@aimmo/ui';
import { z } from 'zod';
import { api, assertOk } from '../lib/api.js';
import { makeLookup } from '../lib/lookup.js';

/**
 * The macro → tipologia → categoria hierarchy (docs/domain/03-lookups-and-enums.md).
 *
 * The level is not an editable field: it follows from the parent, and the API
 * derives it. Offering it as a select would let a user save a contradiction.
 */
const LEVEL_TONES: Record<TypologyLevel, 'accent' | 'outline' | 'neutral'> = {
  macro: 'accent',
  typology: 'outline',
  category: 'neutral',
};

// Only macro and typology rows may be a parent — a category is a leaf.
//
// Asked of the API by level — it filters on one level at a time — rather than
// fetching fifty rows and throwing the categories away, which left the list
// short whenever the categories crowded the parents out of the page.
const parentsAt = async (level: TypologyLevel, query: string, signal: AbortSignal) =>
  assertOk(
    await api.propertyTypologies.list({
      query: {
        page: 1,
        pageSize: 20,
        order: 'asc',
        sort: 'path',
        level,
        ...(query ? { q: query } : {}),
      },
      fetchOptions: { signal },
    }),
  ).body.items;

const { search: searchParents, resolve: resolveParent } = makeLookup({
  key: resourceKeys.all('property-typologies'),
  search: async (query, signal) => {
    const [macros, typologies] = await Promise.all([
      parentsAt('macro', query, signal),
      parentsAt('typology', query, signal),
    ]);
    // Path order puts each typology under its macro, as the tree reads.
    return [...macros, ...typologies].sort((a, b) => a.path.localeCompare(b.path));
  },
  get: async (id, signal) =>
    assertOk(await api.propertyTypologies.get({ params: { id }, fetchOptions: { signal } })).body,
  toOption: (item) => ({ value: item.id, label: item.breadcrumb.join(' › ') }),
});

/**
 * The contract lets `parentId` be omitted (it defaults to null server-side);
 * the form always renders the field, so here it is simply nullable. Same rule,
 * one less optional to thread through the kit's generics.
 */
const typologyFormSchema = propertyTypologyCreateSchema.extend({
  parentId: z.string().uuid().nullable(),
});

export const propertyTypologiesResource = defineResource<
  PropertyTypology,
  PropertyTypologyInput,
  PropertyTypologyInput,
  Partial<PropertyTypologyInput>
>({
  key: 'property-typologies',
  label: { one: 'Tipologia', many: 'Tipologie', gender: 'f' },
  client: tsRestResourceClient(api.propertyTypologies),
  getRowId: (row) => row.id,
  // Sorted by path, so the tree reads as a tree even in a flat table.
  defaultState: { sort: 'path', order: 'asc' },
  search: { placeholder: 'Cerca per codice o etichetta…' },
  columns: [
    {
      id: 'breadcrumb',
      header: 'Tipologia',
      sortKey: 'path',
      accessor: (row) => row.breadcrumb.join(' › '),
    },
    {
      id: 'level',
      header: 'Livello',
      sortKey: 'level',
      width: '10rem',
      cell: ({ row }) => (
        <Badge tone={LEVEL_TONES[row.level]}>{TYPOLOGY_LEVEL_LABELS[row.level]}</Badge>
      ),
    },
    { id: 'code', header: 'Codice', sortKey: 'code', width: '12rem' },
    { id: 'sortOrder', header: 'Ordine', sortKey: 'sortOrder', align: 'right', width: '7rem' },
    { id: 'active', header: 'Attiva', width: '7rem' },
  ],
  filters: [
    {
      id: 'level',
      label: 'Livello',
      type: 'enum',
      options: enumOptions(TYPOLOGY_LEVELS, TYPOLOGY_LEVEL_LABELS),
    },
    {
      id: 'parentId',
      label: 'Voce padre',
      type: 'relation',
      search: searchParents,
      resolve: resolveParent,
    },
    { id: 'active', label: 'Stato', type: 'boolean', trueLabel: 'Attiva', falseLabel: 'Disattiva' },
  ],
  form: {
    schema: typologyFormSchema,
    defaultValues: { parentId: null, code: '', label: '', sortOrder: 0, active: true },
    sections: [
      {
        id: 'main',
        title: 'Tipologia',
        fields: [
          {
            name: 'parentId',
            label: 'Voce padre',
            type: 'relation',
            description: 'Vuoto per una macro-tipologia. Il livello segue dal padre.',
            search: searchParents,
            resolve: resolveParent,
          },
          { name: 'code', label: 'Codice', type: 'text', required: true, placeholder: 'RES_APT' },
          { name: 'label', label: 'Etichetta', type: 'text', required: true },
          { name: 'sortOrder', label: 'Ordine', type: 'number', min: 0, max: 9999 },
          { name: 'active', label: 'Attiva', type: 'switch' },
        ],
      },
    ],
  },
  detail: {
    title: (row) => row.breadcrumb.join(' › '),
    sections: [
      {
        id: 'main',
        title: 'Dati',
        fields: [
          { id: 'label', label: 'Etichetta' },
          { id: 'code', label: 'Codice' },
          { id: 'level', label: 'Livello', accessor: (row) => TYPOLOGY_LEVEL_LABELS[row.level] },
          { id: 'parentLabel', label: 'Voce padre', accessor: (row) => row.parentLabel ?? '—' },
          { id: 'path', label: 'Percorso' },
          { id: 'sortOrder', label: 'Ordine' },
          { id: 'active', label: 'Attiva' },
        ],
      },
    ],
  },
  empty: {
    title: 'Nessuna tipologia',
    description: 'La gerarchia classifica gli immobili. Crea una macro-tipologia per iniziare.',
  },
});
